import React from "react";

interface Props {
  isOpen:        boolean;
  onClose:       () => void;
  title:         string;                 // e.g. "Profile Photo" / "Clinic Logo"
  subtitle?:     string;
  /** Noun used in the action copy, e.g. "photo" or "logo". */
  noun?:         string;
  uploadHint?:   string;
  hasImage:      boolean;
  uploading:     boolean;
  deleting:      boolean;
  onUploadClick: () => void;             // open the file picker
  onDelete:      () => void;             // remove the current image
}

/** Shared upload/delete modal for image assets (profile photo, clinic logo, …). */
export const ImageActionsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  title,
  subtitle = "Upload a new image or remove the current one",
  noun = "image",
  uploadHint = "JPG or PNG, up to 5 MB",
  hasImage,
  uploading,
  deleting,
  onUploadClick,
  onDelete,
}) => {
  if (!isOpen) return null;

  const busy = uploading || deleting;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {/* Upload */}
          <button
            type="button"
            onClick={onUploadClick}
            disabled={busy}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:bg-blue-50 hover:border-blue-200 disabled:opacity-50 transition-colors text-left"
          >
            <span className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-medium text-gray-900">
                {uploading ? "Uploading…" : hasImage ? `Upload new ${noun}` : `Upload ${noun}`}
              </span>
              <span className="text-xs text-gray-400">{uploadHint}</span>
            </span>
          </button>

          {/* Delete */}
          <button
            type="button"
            onClick={onDelete}
            disabled={busy || !hasImage}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:bg-red-50 hover:border-red-200 disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:border-gray-200 transition-colors text-left"
          >
            <span className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-medium text-gray-900">
                {deleting ? "Removing…" : `Delete ${noun}`}
              </span>
              <span className="text-xs text-gray-400">
                {hasImage ? `Remove the current ${noun}` : `No ${noun} to remove`}
              </span>
            </span>
          </button>
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
